package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.db.SettingData
import com.neurobs.ccab.{ObjectResult, SettingValueCreateData}
import com.neurobs.js.ccab.CCABServer
import com.neurobs.ccab.{CCABServer => CCABServerNew}
import com.neurobs.js.controller.{AdminPanelController, AdminPanelDelegate}
import com.neurobs.js.dom.{AnchorLink, ElementFactory, InputTextBox}
import com.neurobs.js.dom.ElementFactory.PanelView
import com.neurobs.js.view.{Container, Form, Gui, ValueSelectBox}
import org.scalajs.dom.raw.{Event, HTMLElement}
import com.neurobs.js.dom.Grid._
import com.neurobs.js.dom.DOMViewElement._
import com.neurobs.js.http
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.util.StringUtilities
import org.scalajs.dom.html.Div
import scalatags.JsDom.all._

import scala.concurrent.ExecutionContext.Implicits.global
import scala.collection.mutable.ArrayBuffer

class AccountController(isAccountAdmin: Boolean, accountId: Long, currentUserId: Long, primaryEmail: String, server: CCABServerNew, cache: RecordCache, gui: Gui, legacy: Boolean = true) extends AdminPanelController {
	private val propertiesDiv = div().render
	private val settingsDiv = div().render
	private val panel: PanelView = {
		val usersButton = ElementFactory.makeButton(caption = "Manage Users")
		usersButton.setHandler(() => {
			delegate.foreach(_.newPanel(UserListController(accountId = accountId, primaryEmail = primaryEmail, currentUserId = currentUserId, server = server, gui = gui, legacy = legacy)))
		})

		val temp = new PanelView(title = if (legacy) s"Account $accountId ($primaryEmail)" else "Account", showRefresh = true, fullWidth = true)
		if (legacy) {
			val closeButton = ElementFactory.makeLink(text = "Close")
			closeButton.onclick = (_: Event) => delegate.foreach(_.close())
			temp.setRightCorner(closeButton)
		}
		temp.refresh.setHandler(update)
		temp.grid.addRow(propertiesDiv)
		temp.grid.addRow(settingsDiv)
		if (isAccountAdmin) temp.grid.addRow(usersButton)
		temp
	}

	private def handleError(message: String, d: Div): Unit = {
		d.innerHTML = ""
		d.appendChild(message)
	}

	private def handleResponse[A](response: http.ServerObjectResponse[ObjectResult[A]], d: Div)(processor: (Option[A]) => Unit): Unit = {
		response match {
			case Left(error) => handleError(error.message, d)
			case Right(res) => res.error match {
				case Some(error) => handleError(error, d)
				case None => processor(res.data)
			}
		}
	}

	def updateSettingValue(setting: SettingData, value: Option[String])(): Unit = {
		val boxWidth = 20
		val formWidth = 40
		val editBox: InputTextBox = makeTextInput(boxWidth)
		editBox.setValue(value.getOrElse(""))
		val inputSelectBox = makeSelectBox(width = boxWidth)
		val selectBox = new ValueSelectBox[String, String](inputSelectBox, s => s, s => s)
		setting.values foreach { option =>
			io.circe.parser.decode[Seq[String]](option).foreach(selectBox.setItems(_))
			selectBox.setSelectedValue(value)
		}
		val form = makeForm(
			width = formWidth,
			title = s"Update ${setting.displayName}",
			description = setting.description,
			elements = Array((setting.displayName, if (setting.values.isDefined) inputSelectBox else editBox.text)),
			useCancel = true,
			submitCaption = "Update"
		)

		def process(callback: Option[String] => Unit): Either[String, Form.Process] = {
			val value = if (setting.values.isDefined) selectBox.value
				else if (editBox.value().isEmpty) None else Some(editBox.value())
			val data = SettingValueCreateData(
				settingId = setting.id,
				userId = None,
				accountId = Some(accountId),
				value = value
			)
			Right(
				CCABServer.setSettingValue(data)(http.generalHandler(callback){ () =>
					callback(None)
					update()
				})
			)
		}

		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}

	private def update(): Unit = {
		server.getAccount(accountId)._2 map {
			case Left(_) => handleError("No account was returned.", propertiesDiv)
			case Right(data) =>
				val t = ElementFactory.makeHeaderlessTable()
				t.appendChild(ElementFactory.makeTableRow(List("Created:", data.createdAt)))
				t.appendChild(ElementFactory.makeTableRow(List("Creator Email:", data.founderEmail)))
				t.appendChild(ElementFactory.makeTableRow(List("Organization:", data.organization)))
				t.appendChild(ElementFactory.makeTableRow(List("Description:", data.userDescription)))
				propertiesDiv.innerHTML = ""
				propertiesDiv.appendChild(t)
		}
		cache.settings.get map {
			case Left(error) => handleError(error.description, settingsDiv)
			case Right(settings) =>
				CCABServer.getSettingValues(settingId = None, userId = None, accountId = Some(accountId)) {
					case Left(error) => handleError(error.message, settingsDiv)
					case Right(values) =>
						val t = ElementFactory.makeHeaderlessTable()
						for (s <- settings if (s.accountSetting && s.visible)) {
							val value =  values.find(_.settingId == s.id).map(_.value)
							val updateLink: AnchorLink = ElementFactory.makeLink(text = "Update")
							updateLink.setHandler(updateSettingValue(setting = s, value = value))
							t.appendChild(ElementFactory.makeTableRow(List(ElementFactory.addTooltip(s.displayName, s.description), span(value.getOrElse("Not set"): String, "  (", updateLink.anchor, ")").render)))
						}
						settingsDiv.clear()
						settingsDiv.appendChild(t)
				}
		}

		propertiesDiv.innerHTML = "Retrieving Data..."
		settingsDiv.innerHTML = "Retrieving Data..."
	}

	override def container: Container = panel.container

	update()
}
